import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PlayIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  TrashIcon,
  ClockIcon,
  DocumentTextIcon,
  SpeakerWaveIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Progress from '@/components/ui/Progress';

// Mock job data
const mockJob = {
  id: 'job_abc123',
  title: 'Le Petit Prince',
  status: 'completed',
  voice: 'Cath Brunet',
  voice_id: 'RFBMQGMs3dbpbuSvswMJ',
  pages: 96,
  chapters: 27,
  duration: '2h 34m',
  fileSize: '156 MB',
  createdAt: '2024-01-15T10:30:00Z',
  completedAt: '2024-01-15T11:30:00Z',
  processingTime: '1h 0m',
  priority: false,
  skipOcr: false,
};

const pipelineSteps = [
  { name: 'PDF Ingestion', status: 'completed', duration: '2m' },
  { name: 'Image Processing', status: 'completed', duration: '15m' },
  { name: 'Chapter Detection', status: 'completed', duration: '3m' },
  { name: 'TTS Synthesis', status: 'completed', duration: '35m' },
  { name: 'Audio Assembly', status: 'completed', duration: '5m' },
  { name: 'Credits Generation', status: 'completed', duration: '1m' },
  { name: 'QA Finalize', status: 'completed', duration: '2m' },
];

const events = [
  { time: '11:30:00', event: 'Job completed successfully', type: 'success' },
  { time: '11:28:00', event: 'QA finalization complete', type: 'info' },
  { time: '11:25:00', event: 'Credits generated', type: 'info' },
  { time: '11:20:00', event: 'Audio assembly complete (27 chapters)', type: 'info' },
  { time: '10:45:00', event: 'TTS synthesis complete (89 batches)', type: 'info' },
  { time: '10:35:00', event: 'Chapter detection found 27 chapters', type: 'info' },
  { time: '10:32:00', event: 'Image processing complete (96 pages)', type: 'info' },
  { time: '10:32:00', event: 'PDF ingestion complete', type: 'info' },
  { time: '10:30:00', event: 'Job started', type: 'info' },
];

function getStatusBadge(status: string) {
  switch (status) {
    case 'completed':
      return <Badge variant="success" size="lg">Completed</Badge>;
    case 'running':
      return <Badge variant="primary" size="lg">Running</Badge>;
    case 'pending':
      return <Badge variant="warning" size="lg">Pending</Badge>;
    case 'failed':
      return <Badge variant="danger" size="lg">Failed</Badge>;
    default:
      return <Badge variant="slate" size="lg">{status}</Badge>;
  }
}

function getStepStatus(status: string) {
  switch (status) {
    case 'completed':
      return '✅';
    case 'running':
      return '🔄';
    case 'pending':
      return '⏳';
    case 'failed':
      return '❌';
    default:
      return '○';
  }
}

export default function TTSJobDetailPage() {
  const { jobId } = useParams();

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back Button */}
      <Link
        to="/tts/jobs"
        className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Jobs
      </Link>

      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-slate-800">{mockJob.title}</h1>
            {getStatusBadge(mockJob.status)}
          </div>
          <p className="text-slate-500 font-mono">{jobId || mockJob.id}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          {mockJob.status === 'completed' && (
            <>
              <Link to={`/tts/player/${mockJob.id}`}>
                <Button leftIcon={<PlayIcon className="w-4 h-4" />}>
                  Play Audio
                </Button>
              </Link>
              <Button variant="outline" leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}>
                Download
              </Button>
            </>
          )}
          {mockJob.status === 'failed' && (
            <Button variant="secondary" leftIcon={<ArrowPathIcon className="w-4 h-4" />}>
              Retry Job
            </Button>
          )}
          <Link to={`/tts/jobs/${mockJob.id}/monitor`}>
            <Button variant="outline" leftIcon={<ChartBarIcon className="w-4 h-4" />}>
              View Monitor
            </Button>
          </Link>
          <Button variant="ghost" className="text-danger-500" leftIcon={<TrashIcon className="w-4 h-4" />}>
            Delete
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
              <DocumentTextIcon className="w-6 h-6 text-primary-500" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Pages</p>
              <p className="text-xl font-bold text-slate-800">{mockJob.pages}</p>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-secondary-100 flex items-center justify-center">
              <SpeakerWaveIcon className="w-6 h-6 text-secondary-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Chapters</p>
              <p className="text-xl font-bold text-slate-800">{mockJob.chapters}</p>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-success-100 flex items-center justify-center">
              <ClockIcon className="w-6 h-6 text-success-500" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Duration</p>
              <p className="text-xl font-bold text-slate-800">{mockJob.duration}</p>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
              <ArrowDownTrayIcon className="w-6 h-6 text-slate-500" />
            </div>
            <div>
              <p className="text-sm text-slate-500">File Size</p>
              <p className="text-xl font-bold text-slate-800">{mockJob.fileSize}</p>
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Job Details */}
        <Card className="lg:col-span-1">
          <CardHeader title="Job Details" />
          <CardBody className="space-y-4">
            <div>
              <p className="text-sm text-slate-500">Voice</p>
              <p className="font-medium text-slate-800">{mockJob.voice}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Voice ID</p>
              <p className="font-mono text-sm text-slate-600">{mockJob.voice_id}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Created</p>
              <p className="font-medium text-slate-800">
                {new Date(mockJob.createdAt).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Completed</p>
              <p className="font-medium text-slate-800">
                {mockJob.completedAt ? new Date(mockJob.completedAt).toLocaleString() : '--'}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Processing Time</p>
              <p className="font-medium text-slate-800">{mockJob.processingTime}</p>
            </div>
            <div className="pt-4 border-t border-slate-100">
              <p className="text-sm text-slate-500 mb-2">Options</p>
              <div className="flex flex-wrap gap-2">
                <Badge variant={mockJob.priority ? 'secondary' : 'slate'}>
                  {mockJob.priority ? 'Priority' : 'Normal'}
                </Badge>
                <Badge variant={mockJob.skipOcr ? 'primary' : 'slate'}>
                  {mockJob.skipOcr ? 'Skip OCR' : 'With OCR'}
                </Badge>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Pipeline Progress */}
        <Card className="lg:col-span-2">
          <CardHeader title="Pipeline Progress" />
          <CardBody>
            <div className="space-y-4">
              {pipelineSteps.map((step, _index) => (
                <div key={step.name} className="flex items-center gap-4">
                  <div className="w-8 text-center text-xl">{getStepStatus(step.status)}</div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium text-slate-800">{step.name}</p>
                      <span className="text-sm text-slate-500">{step.duration}</span>
                    </div>
                    <Progress
                      value={step.status === 'completed' ? 100 : step.status === 'running' ? 50 : 0}
                      size="sm"
                      variant={step.status === 'completed' ? 'success' : 'primary'}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Event Timeline */}
      <Card>
        <CardHeader title="Event Timeline" subtitle="Recent processing events" />
        <CardBody>
          <div className="space-y-4">
            {events.map((event, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="text-sm text-slate-400 w-20">{event.time}</div>
                <div
                  className={`w-2 h-2 mt-1.5 rounded-full ${
                    event.type === 'success' ? 'bg-success-500' : 'bg-primary-400'
                  }`}
                />
                <p className="text-slate-700">{event.event}</p>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

